import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { GuideBlock, ParsedGuide } from './parseAgencyOnboardingGuide';

function InlineEmphasis({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
          return (
            <strong key={i} className="font-semibold text-glass-text">
              {p.slice(2, -2)}
            </strong>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

function BlockView({ block, onImageClick }: { block: GuideBlock; onImageClick: (src: string) => void }) {
  if (block.type === 'paragraph') {
    return (
      <p className="text-sm leading-relaxed text-glass-text-2 md:text-[15px]">
        <InlineEmphasis text={block.text} />
      </p>
    );
  }
  if (block.type === 'list') {
    return (
      <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-glass-text-2 md:text-[15px]">
        {block.items.map((item, i) => (
          <li key={i}>
            <InlineEmphasis text={item} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {block.srcs.map((src, i) => (
        <button
          key={`${src}-${i}`}
          type="button"
          onClick={() => onImageClick(src)}
          className="block w-full overflow-hidden rounded-lg border border-glass bg-glass-2/50 text-left ring-1 ring-black/5 transition hover:ring-[#01696f]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01696f] dark:ring-white/10"
          aria-label={`View screenshot ${i + 1} larger`}
        >
          <img
            src={src}
            alt={`Screenshot ${i + 1}`}
            loading="lazy"
            decoding="async"
            className="h-auto w-full object-cover object-top"
          />
        </button>
      ))}
    </div>
  );
}

export default function AgencyOnboardingGuideDoc({ guide }: { guide: ParsedGuide }) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!lightboxSrc) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lightboxSrc]);

  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxSrc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc]);

  return (
    <div className="space-y-8 border-t border-glass pt-10">
      <header className="space-y-2">
        <h2 className="text-xl font-bold tracking-tight text-glass-text md:text-2xl">{guide.introTitle}</h2>
        {guide.introQuote ? (
          <blockquote className="border-l-4 border-[#01696f]/50 bg-[#01696f]/[0.06] py-3 pl-4 text-sm italic leading-relaxed text-glass-text-2 dark:bg-[#01696f]/10">
            {guide.introQuote}
          </blockquote>
        ) : null}
      </header>

      <ol className="space-y-10">
        {guide.steps.map((step) => (
          <li key={step.number} className="scroll-mt-24">
            <h3 className="mb-4 text-base font-semibold text-glass-text md:text-lg">
              <span className="mr-2 text-[#01696f]">Step {step.number}.</span>
              {step.title}
            </h3>
            <div className="space-y-4">
              {step.blocks.map((block, bi) => (
                <BlockView key={bi} block={block} onImageClick={setLightboxSrc} />
              ))}
            </div>
          </li>
        ))}
      </ol>

      {lightboxSrc
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-[2px]"
              role="dialog"
              aria-modal="true"
              aria-label="Screenshot preview"
              onClick={() => setLightboxSrc(null)}
            >
              <button
                type="button"
                className="absolute right-3 top-3 z-[101] flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white shadow-lg ring-1 ring-white/30 transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxSrc(null);
                }}
                aria-label="Close"
              >
                <X className="h-6 w-6" strokeWidth={2.25} />
              </button>
              <img
                src={lightboxSrc}
                alt=""
                className="max-h-[min(95vh,100%)] max-w-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
